import { useState } from 'react';
import { api } from '../api';
import { useLive } from '../live';

/**
 * Systems: the timezone, the light programs, and the irrigation zones.
 *
 * Until this view existed the portal could claim a controller and run a
 * single module, and everything programmatic - a photoperiod, a mist cycle -
 * was a curl command. These are the same endpoints the curl hit, in the
 * portal's plain style.
 *
 * The timezone matters more than it looks: every photoperiod's on/off is
 * written in it, it rides the runtime config to the controllers, and a
 * system left on UTC runs its "6am" sunrise at whatever hour UTC's 6am lands
 * locally. The list offered is exactly the zones the firmware's tz table
 * knows, so what you pick here is what the shelf can keep offline.
 */

// Mirrors esp32/main/tz.c. A zone not in this list would run in UTC on the
// controller the moment the WAN dropped, so it is not offered.
const TIMEZONES = [
  'America/New_York', 'America/Detroit', 'America/Toronto', 'America/Chicago',
  'America/Winnipeg', 'America/Denver', 'America/Edmonton', 'America/Phoenix',
  'America/Los_Angeles', 'America/Vancouver', 'America/Anchorage',
  'Pacific/Honolulu', 'America/Halifax', 'America/St_Johns',
  'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Europe/Berlin',
  'Europe/Paris', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam',
  'Europe/Stockholm', 'Europe/Warsaw',
  'Australia/Sydney', 'Australia/Perth', 'Asia/Tokyo', 'Asia/Kolkata',
  'UTC',
];

const minutesToTime = (minutes) => {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
};

const timeToMinutes = (value) => {
  const [h, m] = value.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const photoperiodHours = (on, off) => {
  const span = ((off - on) % 1440 + 1440) % 1440;
  return Math.round((span / 60) * 10) / 10;
};

export default function SystemPrograms({ systems, error, loading, refresh }) {
  if (loading && systems.length === 0) {
    return <p className="faint">Looking for your systems…</p>;
  }

  return (
    <div className="stack">
      <div>
        <h2>Systems</h2>
        <p className="muted" style={{ margin: 0 }}>
          Where the programs live: the photoperiod, the watering, and the
          timezone they both run in.
        </p>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      {systems.length === 0 && <CreateSystem onCreated={refresh} />}

      {systems.map((system) => (
        <SystemCard key={system.id} system={system} onChanged={refresh} />
      ))}
    </div>
  );
}

function CreateSystem({ onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const system = await api.createSystem(name || 'My Garden', 'kitchen_shelf');
      // The browser knows the right zone; starting there beats starting on
      // UTC, and the picker on the card corrects it if the guess is off.
      const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (TIMEZONES.includes(guess)) {
        await api.updateSystem(system.id, { timezone: guess });
      }
      onCreated();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit}>
      <div>
        <h3>Name your system</h3>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          A system groups the controllers, zones and programs of one setup.
          Programs need one before they have anywhere to live.
        </p>
      </div>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="sysname">Call it</label>
          <input id="sysname" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Kitchen shelf" />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create it'}
        </button>
      </div>
      {error && <div className="notice notice-error">{error}</div>}
    </form>
  );
}

function SystemCard({ system, onChanged }) {
  const [error, setError] = useState(null);

  async function setTimezone(timezone) {
    setError(null);
    try {
      await api.updateSystem(system.id, { timezone });
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  }

  const zoneKnown = TIMEZONES.includes(system.timezone);

  return (
    <div className="card stack">
      <div className="spread" style={{ alignItems: 'flex-end' }}>
        <div>
          <h3>{system.name}</h3>
          <span className="faint">{system.kind.replace(/_/g, ' ')}</span>
        </div>
        <div style={{ flex: '0 0 240px' }}>
          <label htmlFor={`tz-${system.id}`}>Timezone</label>
          <select id={`tz-${system.id}`} value={system.timezone}
                  onChange={(e) => setTimezone(e.target.value)}>
            {!zoneKnown && (
              <option value={system.timezone}>{system.timezone}</option>
            )}
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </div>
      </div>

      {!zoneKnown && (
        <div className="notice notice-info">
          The controllers cannot keep this timezone offline, so they would
          fall back to UTC without the cloud. Pick one from the list to keep
          the photoperiod honest through an outage.
        </div>
      )}
      {system.timezone === 'UTC' && zoneKnown && (
        <div className="notice notice-info">
          This system is on UTC, so a 6am sunrise happens at UTC's 6am. Pick
          your real timezone above unless you mean it.
        </div>
      )}

      {error && <div className="notice notice-error">{error}</div>}

      <LightPrograms system={system} />
      <Zones system={system} onChanged={onChanged} />
    </div>
  );
}

/* ===== Light programs =================================================== */

function LightPrograms({ system }) {
  const { data, error, refresh } = useLive(() => api.lightPrograms(system.id),
                                           [system.id]);
  const programs = data?.programs || [];
  const [adding, setAdding] = useState(false);

  return (
    <div className="stack" style={{ gap: 'var(--s4)' }}>
      <div>
        <span className="label">Light</span>
        <p className="muted" style={{ margin: '2px 0 0' }}>
          The photoperiod runs on the controller, so it holds through any
          internet or cloud failure once the shelf has seen a clock.
        </p>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      {programs.length === 0 && !adding && (
        <p className="faint" style={{ margin: 0 }}>
          No light program yet. Without one, grow lights only run when you
          press Run.
        </p>
      )}

      {programs.map((program) => (
        <LightProgramRow key={program.id} program={program} onChanged={refresh} />
      ))}

      {adding ? (
        <LightProgramForm systemId={system.id}
                          onDone={() => { setAdding(false); refresh(); }}
                          onCancel={() => setAdding(false)} />
      ) : (
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}
                style={{ alignSelf: 'flex-start' }}>
          Add a light program
        </button>
      )}
    </div>
  );
}

function LightProgramRow({ program, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function toggle() {
    setBusy(true); setError(null);
    try {
      await api.updateLightProgram(program.id, { enabled: !program.enabled });
      onChanged();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true); setError(null);
    try { await api.deleteLightProgram(program.id); onChanged(); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  if (editing) {
    return (
      <LightProgramForm program={program}
                        onDone={() => { setEditing(false); onChanged(); }}
                        onCancel={() => setEditing(false)} />
    );
  }

  return (
    <div className="stack" style={{ gap: 'var(--s2)' }}>
      <div className="spread">
        <div>
          <strong>{program.name}</strong>{' '}
          <span className="faint">
            {minutesToTime(program.onMinutes)} to {minutesToTime(program.offMinutes)}
            {' · '}{photoperiodHours(program.onMinutes, program.offMinutes)}h
            {' · '}{program.intensityPercent}%
            {(program.rampUpMinutes > 0 || program.rampDownMinutes > 0) &&
              ` · ${program.rampUpMinutes}/${program.rampDownMinutes}min ramps`}
            {program.darkPeriodLockout && ' · dark period locked'}
          </span>
        </div>
        <div className="row">
          <span className={`pill ${program.enabled ? 'pill-online' : 'pill-offline'}`}>
            <span className={`dot ${program.enabled ? 'live' : ''}`} />
            {program.enabled ? 'Running' : 'Off'}
          </span>
          <button className="btn btn-quiet btn-sm" onClick={toggle} disabled={busy}>
            {program.enabled ? 'Switch off' : 'Switch on'}
          </button>
          <button className="btn btn-quiet btn-sm" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn btn-quiet btn-sm" onClick={remove} disabled={busy}>
            Delete
          </button>
        </div>
      </div>
      {error && <div className="notice notice-error">{error}</div>}
    </div>
  );
}

function LightProgramForm({ systemId, program, onDone, onCancel }) {
  const [name, setName] = useState(program?.name || 'Photoperiod');
  const [on, setOn] = useState(minutesToTime(program?.onMinutes ?? 360));
  const [off, setOff] = useState(minutesToTime(program?.offMinutes ?? 1320));
  const [intensity, setIntensity] = useState(program?.intensityPercent ?? 100);
  const [rampUp, setRampUp] = useState(program?.rampUpMinutes ?? 15);
  const [rampDown, setRampDown] = useState(program?.rampDownMinutes ?? 15);
  const [lockout, setLockout] = useState(program?.darkPeriodLockout ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const hours = photoperiodHours(timeToMinutes(on), timeToMinutes(off));

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(null);
    const fields = {
      name,
      on_minutes: timeToMinutes(on),
      off_minutes: timeToMinutes(off),
      intensity_percent: Number(intensity),
      ramp_up_minutes: Number(rampUp),
      ramp_down_minutes: Number(rampDown),
      dark_period_lockout: lockout,
    };
    try {
      if (program) {
        await api.updateLightProgram(program.id, fields);
      } else {
        // Created off, switched on deliberately - the same posture as the
        // climate programs. Nothing lights until you flip it.
        await api.createLightProgram(systemId, { ...fields, enabled: false });
      }
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 'var(--s4)' }}>
      <h3>{program ? 'Edit the light program' : 'A light program'}</h3>

      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label htmlFor="lpname">Call it</label>
          <input id="lpname" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div style={{ flex: '0 0 110px' }}>
          <label htmlFor="lpon">Lights on</label>
          <input id="lpon" type="time" value={on} required
                 onChange={(e) => setOn(e.target.value)} />
        </div>
        <div style={{ flex: '0 0 110px' }}>
          <label htmlFor="lpoff">Lights off</label>
          <input id="lpoff" type="time" value={off} required
                 onChange={(e) => setOff(e.target.value)} />
        </div>
        <span className="faint" style={{ paddingBottom: '10px' }}>{hours}h of light</span>
      </div>

      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label htmlFor="lpint">
            Intensity {intensity}%
          </label>
          <input id="lpint" type="range" min={10} max={100} value={intensity}
                 onChange={(e) => setIntensity(e.target.value)}
                 style={{ padding: 0, background: 'transparent', border: 'none' }} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <label htmlFor="lpup">Sunrise (min)</label>
          <input id="lpup" type="number" min={0} max={240} value={rampUp}
                 onChange={(e) => setRampUp(e.target.value)} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <label htmlFor="lpdown">Sunset (min)</label>
          <input id="lpdown" type="number" min={0} max={240} value={rampDown}
                 onChange={(e) => setRampDown(e.target.value)} />
        </div>
      </div>

      <label className="row" style={{ gap: 'var(--s2)', cursor: 'pointer' }}>
        <input type="checkbox" checked={lockout}
               onChange={(e) => setLockout(e.target.checked)}
               style={{ width: 'auto' }} />
        <span>
          Protect the dark period
          <span className="faint"> — refuse manual light during the off hours</span>
        </span>
      </label>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : program ? 'Save' : 'Create it'}
        </button>
        <button className="btn btn-quiet" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* ===== Zones ============================================================ */

const ZONE_TYPES = [
  ['aeroponic', 'Aeroponic (mist on bare roots)'],
  ['dwc', 'Deep water (Kratky or bubbler)'],
  ['nft', 'Nutrient film'],
  ['ebb_flow', 'Flood and drain'],
  ['drip', 'Drip'],
  ['soil', 'Soil'],
  ['wick', 'Wick'],
];

const ZONE_MODES = [
  ['mist_cycle', 'Mist cycle — on for a few seconds, every interval'],
  ['interval', 'Interval — timed watering'],
  ['moisture', 'Moisture — water when the probe reads dry'],
  ['continuous', 'Continuous'],
];

function Zones({ system, onChanged }) {
  const zones = system.zones || [];
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  async function remove(zone) {
    setError(null);
    try { await api.deleteZone(zone.id); onChanged(); }
    catch (e) { setError(e.message); }
  }

  return (
    <div className="stack" style={{ gap: 'var(--s4)' }}>
      <div>
        <span className="label">Water</span>
        <p className="muted" style={{ margin: '2px 0 0' }}>
          A zone is one group of plants watered together. Passive jars need
          none; a pump needs a zone to tell it when.
        </p>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      {zones.map((zone) => (
        <div key={zone.id} className="spread">
          <div>
            <strong>{zone.name}</strong>{' '}
            <span className="faint">
              {zone.zoneType} · {zone.mode.replace(/_/g, ' ')}
              {zone.mode !== 'moisture' &&
                ` · ${zone.onSeconds}s every ${Math.round(zone.intervalSeconds / 60)}min`}
              {zone.mode === 'moisture' && zone.moistureThreshold != null &&
                ` · below ${zone.moistureThreshold}%`}
            </span>
          </div>
          <button className="btn btn-quiet btn-sm" onClick={() => remove(zone)}>
            Delete
          </button>
        </div>
      ))}

      {adding ? (
        <ZoneForm systemId={system.id}
                  onDone={() => { setAdding(false); onChanged(); }}
                  onCancel={() => setAdding(false)} />
      ) : (
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}
                style={{ alignSelf: 'flex-start' }}>
          Add a zone
        </button>
      )}
    </div>
  );
}

function ZoneForm({ systemId, onDone, onCancel }) {
  const [name, setName] = useState('');
  const [zoneType, setZoneType] = useState('aeroponic');
  const [mode, setMode] = useState('mist_cycle');
  const [onSeconds, setOnSeconds] = useState(5);
  const [intervalMin, setIntervalMin] = useState(5);
  const [threshold, setThreshold] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.createZone(systemId, {
        name: name || 'Zone',
        zone_type: zoneType,
        mode,
        on_seconds: Number(onSeconds),
        interval_seconds: Number(intervalMin) * 60,
        ...(mode === 'moisture' ? { moisture_threshold: Number(threshold) } : {}),
      });
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 'var(--s4)' }}>
      <h3>A zone</h3>

      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label htmlFor="zname">Call it</label>
          <input id="zname" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Herb shelf" />
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <label htmlFor="ztype">What kind</label>
          <select id="ztype" value={zoneType}
                  onChange={(e) => setZoneType(e.target.value)}>
            {ZONE_TYPES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="zmode">When to water</label>
        <select id="zmode" value={mode} onChange={(e) => setMode(e.target.value)}>
          {ZONE_MODES.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {mode === 'moisture' ? (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 140px' }}>
            <label htmlFor="zthresh">Water below (%)</label>
            <input id="zthresh" type="number" min={1} max={99} value={threshold}
                   onChange={(e) => setThreshold(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 140px' }}>
            <label htmlFor="zon">Run for (s)</label>
            <input id="zon" type="number" min={1} max={300} value={onSeconds}
                   onChange={(e) => setOnSeconds(e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 140px' }}>
            <label htmlFor="zon2">On for (s)</label>
            <input id="zon2" type="number" min={1} max={300} value={onSeconds}
                   onChange={(e) => setOnSeconds(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 160px' }}>
            <label htmlFor="zint">Every (minutes)</label>
            <input id="zint" type="number" min={1} max={720} value={intervalMin}
                   onChange={(e) => setIntervalMin(e.target.value)} />
          </div>
        </div>
      )}

      {error && <div className="notice notice-error">{error}</div>}

      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create it'}
        </button>
        <button className="btn btn-quiet" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
